import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Building2, Network, Users, CalendarClock, Briefcase, ClipboardList,
  Settings, ShieldCheck, FileUp, UserPlus, LayoutGrid,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const CARDS = [
  { to: "/empresas", icon: Building2, label: "Empresas", desc: "Cadastro do grupo econômico e suas empresas", group: "Estrutura" },
  { to: "/setores", icon: Network, label: "Setores & Quadro", desc: "Setores, responsáveis, quadro aprovado, semáforos", group: "Estrutura" },
  { to: "/organograma", icon: LayoutGrid, label: "Organograma", desc: "Visão hierárquica por empresa e setor", group: "Estrutura" },
  { to: "/escalas", icon: CalendarClock, label: "Escalas & Jornadas", desc: "5x2, 6x1, 12x36, turnos e horários", group: "Operação" },
  { to: "/cargos-salarios", icon: Briefcase, label: "Cargos &  Salários", desc: "Plano de cargos com assistente em 5 etapas", group: "Operação" },
  { to: "/colaboradores", icon: Users, label: "Colaboradores", desc: "Cadastro, filtros, paginação e histórico", group: "Pessoas" },
  { to: "/importacao", icon: FileUp, label: "Importação Excel", desc: "Importar colaboradores em lote com prévia", group: "Pessoas" },
  { to: "/admin/usuarios", icon: Settings, label: "Usuários & Perfis", desc: "Gestão de acessos e permissões por perfil", group: "Administração" },
  { to: "/auditoria", icon: ShieldCheck, label: "Auditoria", desc: "Trilha completa de eventos e alterações", group: "Administração" },
];

export default function GestaoPage() {
  const [companies, setCompanies] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [employees, setEmployees] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/companies"),
      api.get("/sectors/summary"),
      api.get("/employees", { params: { page: 1, page_size: 1 } }),
    ]).then(([c, s, e]) => {
      setCompanies(c.data);
      setSectors(s.data);
      setEmployees(e.data.total);
    }).finally(() => setLoading(false));
  }, []);

  const groups = ["Estrutura", "Operação", "Pessoas", "Administração"];

  return (
    <div className="space-y-6" data-testid="gestao-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Hub Administrativo</div>
        <h1 className="font-display text-3xl font-semibold">Gestão do RH</h1>
        <p className="mt-1 text-[13.5px] text-muted-foreground">
          Ponto central de administração: estrutura organizacional, operação, pessoas e controle.
        </p>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Empresas do grupo" value={loading ? null : companies.length} />
        <Card label="Setores ativos" value={loading ? null : sectors?.total_setores} />
        <Card label="Ocupação média" value={loading ? null : `${sectors?.ocupacao_media ?? 0}%`} />
        <Card label="Total de colaboradores" value={loading ? null : employees} tone="primary" />
      </div>

      {/* Groups */}
      {groups.map((g) => (
        <div key={g}>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground font-medium mb-2">{g}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {CARDS.filter(c => c.group === g).map((c) => (
              <Link
                key={c.to}
                to={c.to}
                data-testid={`gestao-card-${c.to.replace(/\//g, "")}`}
                className="group rounded-lg border border-border bg-white p-5 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md hover:border-[#234935]/30"
              >
                <div className="flex items-start gap-3">
                  <div className="grid h-11 w-11 place-items-center rounded-md bg-[#ECF1EE] text-[#234935] group-hover:bg-[#234935] group-hover:text-[#C5A059] transition-colors duration-200">
                    <c.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-[15.5px] font-semibold">{c.label}</div>
                    <div className="text-[12px] text-muted-foreground leading-snug mt-0.5">{c.desc}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Card({ label, value, tone = "default" }) {
  const tones = { default: "bg-white", primary: "bg-[#234935] text-white" };
  const labelColor = tone === "primary" ? "text-white/70" : "text-muted-foreground";
  const valueColor = tone === "primary" ? "text-white" : "text-foreground";
  return (
    <div className={`rounded-lg border border-border ${tones[tone]} p-4`}>
      <div className={`text-[10.5px] uppercase tracking-[0.22em] font-medium ${labelColor}`}>{label}</div>
      {value === null ? <Skeleton className="h-7 w-14 mt-2" /> :
        <div className={`mt-1.5 font-display text-[26px] font-semibold ${valueColor}`}>{value}</div>}
    </div>
  );
}
